import type { NextConfig } from 'next';
// eslint-disable-next-line import/no-named-as-default
import type webpack from 'webpack';
import type { WebpackOptionsNormalized } from 'webpack';
import { SortChunksPlugin } from '../plugins/sort-chunks';
import type { TransformConfigInput, TransformConfigResponse } from './types';

export function transform(args: TransformConfigInput): TransformConfigResponse {
  const { next, microfrontend, opts } = args;

  const configWithWebpack: NextConfig = {
    ...next,
    webpack(cfg, context): unknown {
      // execute the client config first, otherwise their config may accidentally
      // overwrite our required config - leading to unexpected errors.
      const config = (
        typeof next.webpack === 'function' ? next.webpack(cfg, context) : cfg
      ) as WebpackOptionsNormalized;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { isServer, nextRuntime, webpack: wpFromNext } = context;

      if (isServer || nextRuntime === 'edge') {
        config.plugins.push(
          new (wpFromNext as typeof webpack).EnvironmentPlugin({
            MFE_CONFIG: JSON.stringify(microfrontend.serialize()),
          }),
        );
      }

      config.plugins.push(
        // Remove node: from import specifiers, because Next.js does not yet support node: scheme
        // https://github.com/vercel/next.js/issues/28774
        new (wpFromNext as typeof webpack).NormalModuleReplacementPlugin(
          /^node:/,
          (resource) => {
            resource.request = resource.request.replace(/^node:/, '');
          },
        ),
      );

      // Webpack 5 no longer polyfills Node.js core modules automatically which means
      // if you use them in your code running in browsers or alike, you will have to
      // install compatible modules from npm and include them yourself.
      // https://webpack.js.org/configuration/resolve/#resolvefallback
      if (!isServer) {
        config.resolve.fallback = {
          fs: false,
          path: false,
          crypto: false,
          ...config.resolve.fallback,
        };
      }

      if (opts?.supportPagesRouter) {
        // Ensure deterministic module and chunk ids. See
        // https://github.com/vercel/next.js/issues/52827 for more information.
        config.optimization.moduleIds = 'deterministic';
        config.optimization.chunkIds = 'deterministic';

        // Add a custom plugin to sort chunks
        config.plugins.push(new SortChunksPlugin());
      }

      return config;
    },
  };

  return {
    next: configWithWebpack,
  };
}
